/**
 * A person, at a glance: who they are, what they're worth to SyncSuite, how
 * they found us, and the conversation. Everything else (raw fields, consent,
 * custom fields) folds into "Details" so the page stays light.
 *
 * Data: GET /api/contacts/:id (contact + platform accounts + attribution,
 * touches, website visits, conversions sent, affiliate record, tasks).
 */
import { Children, isValidElement, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { backFromState } from "@/lib/back-target";
import {
  ArrowLeft, Mail, Phone, MapPin, Trash2, ExternalLink, BanIcon, User, Cake, Eye, EyeOff, Merge, Flame,
  ChevronDown, BadgeDollarSign, Globe2, Languages, CalendarClock, Activity as ActivityIcon, CircleDollarSign, Sparkles,
} from "lucide-react";
import { Badge, Skeleton, Tooltip, useConfirm, useToast } from "@/components/ui";
import { useMediaQuery } from "@/lib/use-media";
import { ContactInboxPanel } from "@/features/inbox/ContactInboxPanel";
import { ContactAvatarUpload } from "@/features/contacts/AvatarUpload";
import { MergeDialog } from "@/features/contacts/MergeDialog";
import { InlineEditField, InlineEditSelect, InlineEditCheckbox } from "@/features/contacts/InlineEdit";
import {
  AutomationsPanel, ConsentPanel, NotesPanel, TagsPanel, TasksPanel, TimelinePanel,
} from "@/features/contacts/InfoPanels";
import { SyncSuitePanel, type ContactPlatformAccount } from "@/features/contacts/SyncSuitePanel";
import { FoundUsPanel, type Attribution, type ConversionSend, type Touch, type Website } from "@/features/contacts/FoundUsPanel";
import { STAGE_BY_KEY } from "@/features/accounts/platform-meta";
import { dotClass, timeAgo, usd } from "@/features/command/ui";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { fullName, formatPhone, formatDate, formatDateTime } from "@/lib/format";
import type { Contact, CustomField } from "@/lib/types";
import { forgetRecent, trackRecent } from "@/lib/recent";

const LIFECYCLE_OPTIONS = [
  { value: "lead",     label: "Lead" },
  { value: "contact",  label: "Contact" },
  { value: "customer", label: "Customer" },
  { value: "churned",  label: "Churned" },
];

const SOCIAL_NETWORKS: { key: string; label: string; placeholder: string }[] = [
  { key: "instagram", label: "Instagram",  placeholder: "@handle" },
  { key: "tiktok",    label: "TikTok",     placeholder: "@handle" },
  { key: "youtube",   label: "YouTube",    placeholder: "youtube.com/@handle" },
  { key: "facebook",  label: "Facebook",   placeholder: "facebook.com/handle" },
  { key: "linkedin",  label: "LinkedIn",   placeholder: "linkedin.com/in/handle" },
  { key: "twitter",   label: "Twitter / X", placeholder: "@handle" },
  { key: "website",   label: "Website",    placeholder: "https://…" },
];

const LANG: Record<string, string> = { en: "English", es: "Spanish", ja: "Japanese", fr: "French", de: "German", pt: "Portuguese" };
const KIND_LABEL: Record<string, string> = { partner: "JV partner", pro: "Pro affiliate", creator: "Content creator", casual: "Casual sharer" };

interface ContactExtras {
  attribution: Attribution | null;
  source: { id: string; slug: string; name: string; color: string | null } | null;
  touches: Touch[];
  website: Website | null;
  conversions: ConversionSend[];
  affiliate: { id: string; name: string; kind: string; channel: string; status: string; codes: string[]; program: Record<string, unknown> } | null;
}

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const back = backFromState(location.state, { to: "/contacts", label: "People" });
  const toast = useToast();
  const confirm = useConfirm();
  const [contact, setContact] = useState<Contact | null>(null);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [platformAccounts, setPlatformAccounts] = useState<ContactPlatformAccount[]>([]);
  const [extras, setExtras] = useState<ContactExtras | null>(null);
  const [loading, setLoading] = useState(true);
  const [mergeOpen, setMergeOpen] = useState(false);
  const isMedium = useMediaQuery("(min-width: 768px)");
  const isWide = useMediaQuery("(min-width: 1280px)");
  const [detailsOpen, setDetailsOpen] = useState<boolean>(() => {
    try { return localStorage.getItem("crm.contact.details") === "1"; } catch { return false; }
  });
  const [hideEmpty, setHideEmpty] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const saved = window.localStorage.getItem("crm.contact.hideEmpty");
    return saved === null ? true : saved === "1";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("crm.contact.hideEmpty", hideEmpty ? "1" : "0");
  }, [hideEmpty]);
  useEffect(() => {
    try { localStorage.setItem("crm.contact.details", detailsOpen ? "1" : "0"); } catch { /* private mode */ }
  }, [detailsOpen]);

  const load = async (silent = false) => {
    if (!id) return;
    try {
      const [c, cf] = await Promise.all([
        api.get<{ contact: Contact; platform_accounts?: ContactPlatformAccount[] } & Partial<ContactExtras>>(`/contacts/${id}`),
        api.get<{ fields: CustomField[] }>("/custom-fields"),
      ]);
      setContact(c.contact);
      trackRecent({ to: `/contacts/${id}`, label: fullName(c.contact), kind: "person" });
      setPlatformAccounts(c.platform_accounts ?? []);
      setExtras({
        attribution: c.attribution ?? null,
        source: c.source ?? null,
        touches: c.touches ?? [],
        website: c.website ?? null,
        conversions: c.conversions ?? [],
        affiliate: c.affiliate ?? null,
      });
      setCustomFields(cf.fields);
    } catch (err) {
      if (!silent) {
        toast.error(err instanceof ApiError ? err.message : "Failed to load contact");
        if (err instanceof ApiError && err.status === 404) navigate(back.to);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  // Background poll so AI-driven updates (or another tab) propagate.
  useEffect(() => {
    if (!id) return;
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => { if (timer) return; timer = setInterval(() => { void load(true); }, 20000); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const onVis = () => { if (document.hidden) stop(); else start(); };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [id]);

  /** The headline facts from their SyncSuite accounts. */
  const summary = useMemo(() => {
    const live = platformAccounts.filter((a) => a.stage !== "deleted");
    const paid = live.reduce((s, a) => s + (Number(a.revenue_usd) || 0) + (Number(a.lifetime_spend_usd) || 0), 0);
    const rank: Record<string, number> = { customer: 9, trialing: 8, beta: 7, domain: 6, card: 5, published: 4, built: 3, verified: 2, signed_up: 1, canceled: 0 };
    const best = [...live].sort((a, b) => (rank[b.stage] ?? 0) - (rank[a.stage] ?? 0))[0] ?? null;
    const lastActive = live.map((a) => a.last_active_at).filter(Boolean).sort().pop() ?? null;
    const signedUp = live.map((a) => a.platform_created_at).filter(Boolean).sort()[0] ?? null;
    return { paid, best, lastActive, signedUp, count: live.length };
  }, [platformAccounts]);

  /** Optimistic patch on the contact row itself. Tags are managed by TagsPanel. */
  const patch = async (delta: Partial<Contact>) => {
    if (!contact || !id) return;
    const prev = contact;
    setContact({ ...contact, ...delta } as Contact);
    try {
      const r = await api.patch<{ contact: Contact }>(`/contacts/${id}`, delta);
      setContact({ ...r.contact, tags: prev.tags });
    } catch (err) {
      setContact(prev);
      toast.error(err instanceof ApiError ? err.message : "Failed to save");
      throw err;
    }
  };

  const onDelete = async () => {
    if (!id) return;
    const name = contact ? [contact.first_name, contact.last_name].filter(Boolean).join(" ") : null;
    if (!(await confirm({
      title: "Delete this contact?",
      description: (
        <>
          {name ? <strong className="text-zinc-100">{name}</strong> : "This contact"} and
          all their messages, tasks, notes, and activity will be permanently deleted.
          This can't be undone.
        </>
      ),
      confirmLabel: "Delete contact",
      danger: true,
    }))) return;
    try {
      await api.delete(`/contacts/${id}`);
      forgetRecent(`/contacts/${id}`);
      toast.success("Contact deleted");
      navigate(back.to);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="flex h-full flex-col overflow-hidden p-3 sm:p-4 lg:px-6 lg:py-4">
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] xl:grid-cols-12">
          <div className="space-y-3 xl:col-span-4"><Skeleton className="h-56" /><Skeleton className="h-40" /></div>
          <Skeleton className="min-h-64 xl:col-span-5" />
          <div className="hidden space-y-3 xl:col-span-3 xl:block"><Skeleton className="h-32" /><Skeleton className="h-24" /></div>
        </div>
      </div>
    );
  }
  if (!contact) return null;

  const cf = contact.custom_fields ?? {};
  const lang = typeof cf.platform_language === "string" ? (LANG[cf.platform_language] ?? cf.platform_language) : null;
  const tz = typeof cf.platform_timezone === "string" ? cf.platform_timezone : null;
  const localTime = tz ? (() => { try { return new Date().toLocaleTimeString(undefined, { timeZone: tz, hour: "numeric", minute: "2-digit" }); } catch { return null; } })() : null;
  const place = [contact.city, contact.state, contact.country ?? (typeof cf.signup_country === "string" ? cf.signup_country : null)].filter(Boolean).join(", ");
  const best = summary.best;
  const stage = best ? STAGE_BY_KEY[best.stage] : null;
  const layout: "stack" | "two" | "three" = isWide ? "three" : isMedium ? "two" : "stack";
  const work = (
    <>
      <TasksPanel contactId={contact.id} />
      <NotesPanel contactId={contact.id} />
      <AutomationsPanel contactId={contact.id} />
      <TagsPanel contact={contact} onContactChange={(c) => setContact(c)} />
      <TimelinePanel contactId={contact.id} />
    </>
  );

  return (
    <div className="flex h-full flex-col overflow-y-auto md:overflow-hidden">
      {/* Three columns on lg+, stacked on phones */}
      {/* Phones: stacked. Medium (the dashboard's side-by-side frame): the
          person + their work on the left, the conversation full height on the
          right. Wide: person · conversation · work. */}
      <div className="grid flex-1 grid-cols-1 gap-3 px-3 py-3 sm:px-4 md:min-h-0 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] md:gap-4 xl:grid-cols-12 lg:px-6 lg:py-4">
        <aside className="space-y-3 md:min-h-0 md:overflow-y-auto md:pr-1 xl:col-span-4">
          {/* Who they are + what they're worth: one compact card, so the
              conversation keeps the full height of the page. */}
          <div className="cc-card relative overflow-hidden p-4">
            <div className="pointer-events-none absolute -left-16 -top-20 h-44 w-44 rounded-full bg-indigo-500/15 blur-3xl" />
            <div className="relative">
              <div className="-mt-1 mb-2 flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => navigate(back.to)}
                  className="-ml-1.5 inline-flex items-center gap-1 rounded-lg px-1.5 py-1 text-xs font-medium text-zinc-400 transition-colors hover:bg-zinc-800/60 hover:text-zinc-100"
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> {back.label}
                </button>
                <div className="flex-1" />
                <Tooltip content="Merge a duplicate into this person">
                  <button type="button" onClick={() => setMergeOpen(true)} aria-label="Merge duplicate" className="flex h-7 w-7 items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-zinc-800/60 hover:text-zinc-100">
                    <Merge className="h-3.5 w-3.5" />
                  </button>
                </Tooltip>
                <Tooltip content="Delete this person">
                  <button type="button" onClick={onDelete} aria-label="Delete" className="flex h-7 w-7 items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-rose-500/10 hover:text-rose-300">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </Tooltip>
              </div>

              <div className="flex items-start gap-3">
                <div className="shrink-0">
                  <ContactAvatarUpload contact={contact} size="lg" onSaved={(c) => setContact({ ...c, tags: contact.tags })} />
                </div>
                <div className="min-w-0 flex-1">
                  <h1 className="truncate text-lg font-semibold leading-tight text-zinc-50" title={fullName(contact) || undefined}>
                    {fullName(contact) || <span className="italic text-zinc-500">Unnamed</span>}
                  </h1>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {stage && (
                      <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset", stageTone(best!.stage))}>
                        {stage.label}{best?.plan_code && best.plan_code !== "free" ? ` · ${best.plan_code}` : best?.plan_code === "free" ? " · free" : ""}
                      </span>
                    )}
                    {extras?.affiliate && (
                      <Link to={`/affiliates/${extras.affiliate.id}`} state={{ from: location.pathname }} className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-200 ring-1 ring-inset ring-emerald-500/30 hover:brightness-110">
                        <BadgeDollarSign className="h-3 w-3" /> {KIND_LABEL[extras.affiliate.kind] ?? "Affiliate"}
                      </Link>
                    )}
                    {contact.do_not_contact && <Badge color="rose" size="sm"><BanIcon className="mr-1 h-3 w-3" /> Do not contact</Badge>}
                  </div>
                </div>
              </div>

              <div className="mt-3 space-y-1 text-[13px] text-zinc-400">
                {contact.email && <a href={`mailto:${contact.email}`} className="flex min-w-0 items-center gap-2 hover:text-zinc-100"><Mail className="h-3.5 w-3.5 shrink-0 text-zinc-500" /><span className="truncate">{contact.email}</span></a>}
                {contact.phone && <a href={`tel:${contact.phone}`} className="flex items-center gap-2 hover:text-zinc-100"><Phone className="h-3.5 w-3.5 shrink-0 text-zinc-500" />{formatPhone(contact.phone)}</a>}
                {(place || localTime || lang) && (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    {place && <span className="inline-flex items-center gap-2"><MapPin className="h-3.5 w-3.5 shrink-0 text-zinc-500" />{place}</span>}
                    {localTime && <span className="inline-flex items-center gap-2"><Globe2 className="h-3.5 w-3.5 shrink-0 text-zinc-500" />{localTime} their time</span>}
                    {lang && <span className="inline-flex items-center gap-2"><Languages className="h-3.5 w-3.5 shrink-0 text-zinc-500" />{lang}</span>}
                  </div>
                )}
              </div>

              <div className="mt-3 grid grid-cols-2 gap-1.5">
                <Fact icon={CircleDollarSign} label="Paid so far" value={summary.paid > 0 ? usd(summary.paid, { cents: summary.paid % 1 !== 0 }) : "$0"} strong={summary.paid > 0} />
                <Fact icon={Sparkles} label="Came from" value={extras?.source ? (
                  <Link to={`/traffic/${extras.source.slug}`} state={{ from: location.pathname }} className="inline-flex max-w-full items-center gap-1.5 hover:underline">
                    <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", dotClass(extras.source.color))} />
                    <span className="truncate">{extras.source.name}</span>
                  </Link>
                ) : "Unknown"} />
                <Fact icon={CalendarClock} label="Signed up" value={summary.signedUp ? timeAgo(summary.signedUp) : contact.created_at ? timeAgo(contact.created_at) : "–"} />
                <Fact icon={ActivityIcon} label="Last active" value={summary.lastActive ? timeAgo(summary.lastActive) : contact.last_activity_at ? timeAgo(contact.last_activity_at) : "–"} />
              </div>
            </div>
          </div>

          {platformAccounts.length > 0 && <SyncSuitePanel contact={contact} accounts={platformAccounts} hideCameFrom />}
          {extras && (
            <FoundUsPanel
              attribution={extras.attribution}
              source={extras.source}
              touches={extras.touches}
              website={extras.website}
              conversions={extras.conversions}
            />
          )}
          {extras?.affiliate && <AffiliateCard a={extras.affiliate} from={location.pathname} />}

          {/* Details: raw fields, folded away */}
          <div className="cc-card">
            <button type="button" onClick={() => setDetailsOpen((v) => !v)} className="flex w-full items-center justify-between px-4 py-3 text-left">
              <span>
                <span className="block text-sm font-semibold text-zinc-100">Details</span>
                <span className="block text-[11px] text-zinc-500">Name, contact info, address, socials, custom fields, do-not-disturb</span>
              </span>
              <ChevronDown className={cn("h-4 w-4 text-zinc-500 transition-transform", detailsOpen && "rotate-180")} />
            </button>
            {detailsOpen && (
              <div className="space-y-3 border-t border-zinc-800/60 p-3">
                <label className="flex cursor-pointer items-center gap-2.5 px-1 text-xs text-zinc-300">
                  <input type="checkbox" checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)} className="h-3.5 w-3.5 rounded border-zinc-700 bg-zinc-900 text-indigo-500" />
                  {hideEmpty ? <EyeOff className="h-3.5 w-3.5 text-zinc-400" /> : <Eye className="h-3.5 w-3.5 text-zinc-400" />}
                  <span className="flex-1">Hide empty fields</span>
                  <LeadScoreBadge score={contact.lead_score} />
                </label>
                <Card title="Identity">
                  <Row icon={User} label="First name" hidden={hideEmpty && !contact.first_name}>
                    <InlineEditField value={contact.first_name} onSave={(v) => patch({ first_name: v })} label="First name" />
                  </Row>
                  <Row icon={User} label="Last name" hidden={hideEmpty && !contact.last_name}>
                    <InlineEditField value={contact.last_name} onSave={(v) => patch({ last_name: v })} label="Last name" />
                  </Row>
                  <Row icon={Mail} label="Email" hidden={hideEmpty && !contact.email}>
                    <InlineEditField value={contact.email} onSave={(v) => patch({ email: v })} kind="email" label="Email" />
                  </Row>
                  <Row icon={Phone} label="Phone" hidden={hideEmpty && !contact.phone}>
                    <InlineEditField value={contact.phone} onSave={(v) => patch({ phone: v })} kind="tel" label="Phone" formatDisplay={(v) => formatPhone(v)} />
                  </Row>
                  <Row icon={Cake} label="Birthday" hidden={hideEmpty && !contact.date_of_birth}>
                    <InlineEditField value={contact.date_of_birth} onSave={(v) => patch({ date_of_birth: v })} kind="date" label="Date of birth" formatDisplay={(v) => formatDate(v)} />
                  </Row>
                  <Row label="Stage">
                    <InlineEditSelect value={contact.lifecycle_stage ?? "lead"} options={LIFECYCLE_OPTIONS} onSave={(v) => patch({ lifecycle_stage: v })} label="Lifecycle stage" />
                  </Row>
                </Card>
                <Card title="Address">
                  <Row icon={MapPin} label="Street" hidden={hideEmpty && !contact.address_line1}>
                    <InlineEditField value={contact.address_line1} onSave={(v) => patch({ address_line1: v })} label="Street" />
                  </Row>
                  <Row label="City" hidden={hideEmpty && !contact.city}>
                    <InlineEditField value={contact.city} onSave={(v) => patch({ city: v })} label="City" />
                  </Row>
                  <Row label="State / Region" hidden={hideEmpty && !contact.state}>
                    <InlineEditField value={contact.state} onSave={(v) => patch({ state: v })} label="State / Region" />
                  </Row>
                  <Row label="Postal code" hidden={hideEmpty && !contact.postal_code}>
                    <InlineEditField value={contact.postal_code} onSave={(v) => patch({ postal_code: v })} label="Postal code" />
                  </Row>
                  <Row label="Country" hidden={hideEmpty && !contact.country}>
                    <InlineEditField value={contact.country} onSave={(v) => patch({ country: v })} label="Country" />
                  </Row>
                </Card>
                <Card title="Social profiles">
                  {SOCIAL_NETWORKS.map(({ key, label, placeholder }) => {
                    const value = contact.socials[key] ?? "";
                    const isUrl = /^https?:/.test(value);
                    return (
                      <Row key={key} label={label} hidden={hideEmpty && !value}>
                        <div className="flex items-center gap-1">
                          <div className="min-w-0 flex-1">
                            <InlineEditField value={value} onSave={(v) => patch({ socials: { ...contact.socials, [key]: v ?? "" } })} kind="url" label={label} placeholder={placeholder} />
                          </div>
                          {isUrl && (
                            <a href={value} target="_blank" rel="noreferrer" className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800 hover:text-indigo-300" aria-label={`Open ${label}`}>
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      </Row>
                    );
                  })}
                </Card>
                {customFields.filter((f) => !f.key.startsWith("platform_") && !f.key.startsWith("signup_")).length > 0 && (
                  <Card title="Custom fields">
                    {customFields
                      .filter((f) => !f.key.startsWith("platform_") && !f.key.startsWith("signup_"))
                      .map((f) => {
                        const v = contact.custom_fields[f.key];
                        const isEmpty = v == null || v === "" || (Array.isArray(v) && v.length === 0);
                        return (
                          <Row key={f.key} label={f.label} hidden={hideEmpty && isEmpty}>
                            <CustomFieldInlineEdit field={f} value={v} onSave={(next) => patch({ custom_fields: { ...contact.custom_fields, [f.key]: next } })} />
                          </Row>
                        );
                      })}
                  </Card>
                )}
                <ConsentPanel contact={contact} />
              </div>
            )}
          </div>
          {layout === "two" && work}
        </aside>

        {/* The conversation */}
        <section className="min-h-112 md:min-h-0 xl:col-span-5">
          <ContactInboxPanel contact={contact} />
        </section>

        {/* Work and history */}
        {layout === "three" && <aside className="space-y-3 xl:col-span-3 xl:min-h-0 xl:overflow-y-auto xl:pr-1">{work}</aside>}
        {layout === "stack" && <div className="space-y-3">{work}</div>}
      </div>

      <MergeDialog
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        winner={contact}
        onMerged={() => { void load(true); }}
      />
    </div>
  );
}

function stageTone(stage: string): string {
  if (stage === "customer") return "bg-emerald-500/15 text-emerald-200 ring-emerald-500/30";
  if (stage === "trialing" || stage === "beta") return "bg-amber-500/15 text-amber-200 ring-amber-500/30";
  if (stage === "canceled" || stage === "deleted") return "bg-rose-500/15 text-rose-200 ring-rose-500/30";
  return "bg-indigo-500/15 text-indigo-200 ring-indigo-500/30";
}

function Fact({ icon: Icon, label, value, strong }: { icon: React.ComponentType<{ className?: string }>; label: string; value: ReactNode; strong?: boolean }) {
  return (
    <div className="min-w-0 rounded-lg border border-zinc-800 bg-zinc-900/50 px-2.5 py-1.5">
      <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500"><Icon className="h-3 w-3" />{label}</div>
      <div className={cn("mt-0.5 truncate text-[13px] font-semibold", strong ? "cc-gradient-text" : "text-zinc-100")}>{value}</div>
    </div>
  );
}

function AffiliateCard({ a, from }: { a: NonNullable<ContactExtras["affiliate"]>; from: string }) {
  const p = a.program ?? {};
  const n = (k: string) => Number(p[k] ?? 0) || 0;
  return (
    <Link to={`/affiliates/${a.id}`} state={{ from }} className="cc-card cc-card-hover block p-4">
      <div className="flex items-center gap-2">
        <BadgeDollarSign className="h-4 w-4 text-emerald-300" />
        <h3 className="text-sm font-semibold text-zinc-100">They send us customers</h3>
      </div>
      <p className="mt-1 text-xs text-zinc-400">{KIND_LABEL[a.kind] ?? "Affiliate"}{a.codes?.length ? ` · code ${a.codes[0]}` : ""}</p>
      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <div><div className="text-base font-semibold tabular-nums text-zinc-100">{n("referred")}</div><div className="text-[10px] uppercase tracking-wider text-zinc-500">Referred</div></div>
        <div><div className="text-base font-semibold tabular-nums text-zinc-100">{usd(n("gross_usd"))}</div><div className="text-[10px] uppercase tracking-wider text-zinc-500">They brought</div></div>
        <div><div className="text-base font-semibold tabular-nums text-emerald-300">{usd(n("due_usd") + n("pending_usd"))}</div><div className="text-[10px] uppercase tracking-wider text-zinc-500">We owe</div></div>
      </div>
    </Link>
  );
}

// ─── Building blocks for the rails ────────────────────────────────────────
/**
 * Small engagement-heat indicator. lead_score is 0–100; we bucket it into
 * cold (zinc), warm (amber), and hot (emerald) so the color reads at a
 * glance without the user having to interpret the number.
 */
function LeadScoreBadge({ score }: { score: number }) {
  const clamped = Math.max(0, Math.min(100, Math.round(score ?? 0)));
  const color = clamped >= 67 ? "emerald" : clamped >= 34 ? "amber" : "zinc";
  return (
    <Badge color={color} size="sm">
      <Flame className="h-3 w-3 mr-1" /> Lead score {clamped}
    </Badge>
  );
}

// ─── Building blocks for the rails ────────────────────────────────────────
/**
 * Card collapses entirely when every Row inside is hidden. We can't just
 * `filter(Boolean)` the children — Row elements are always truthy regardless
 * of their `hidden` prop. Instead we walk the children, peek at each one's
 * `hidden` prop, and treat the card as empty when every Row passed
 * `hidden=true`.
 */
function Card({ title, children }: { title: string; children: ReactNode }) {
  const arr = Children.toArray(children);
  const anyVisible = arr.some((child) => {
    if (isValidElement(child)) {
      const props = child.props as { hidden?: boolean };
      return !props.hidden;
    }
    return Boolean(child);
  });
  if (!anyVisible) return null;
  return (
    <div className="rounded-xl border border-zinc-800/60 bg-zinc-900/40 p-3">
      <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

/**
 * One field row with the label stacked ABOVE the editor (per the requested
 * layout). Setting `hidden` returns null, which Card detects via the
 * children walk above to collapse the whole section if nothing is showing.
 */
function Row({ icon: Icon, label, children, hidden }: {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  children: ReactNode;
  hidden?: boolean;
}) {
  if (hidden) return null;
  return (
    <div>
      <div className="mb-1 flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-zinc-500">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function CustomFieldInlineEdit({ field, value, onSave }: {
  field: CustomField;
  value: unknown;
  onSave: (next: unknown) => Promise<void>;
}) {
  switch (field.field_type) {
    case "boolean":
      return (
        <InlineEditCheckbox
          value={!!value}
          onSave={(v) => onSave(v)}
          label={value ? "Yes" : "No"}
        />
      );
    case "longtext":
      return (
        <InlineEditField
          value={value == null ? null : String(value)}
          onSave={(v) => onSave(v)}
          kind="longtext"
          label={field.label}
        />
      );
    case "number":
      return (
        <InlineEditField
          value={value == null ? null : String(value)}
          onSave={(v) => onSave(v == null ? null : Number(v))}
          kind="number"
          label={field.label}
        />
      );
    case "date":
      return (
        <InlineEditField
          value={value == null ? null : String(value).slice(0, 10)}
          onSave={(v) => onSave(v)}
          kind="date"
          label={field.label}
          formatDisplay={(v) => formatDate(v)}
        />
      );
    case "datetime":
      return (
        <InlineEditField
          value={value == null ? null : String(value).slice(0, 16)}
          onSave={(v) => onSave(v)}
          kind="datetime"
          label={field.label}
          formatDisplay={(v) => formatDateTime(v)}
        />
      );
    case "url":
    case "email":
    case "phone":
      return (
        <InlineEditField
          value={value == null ? null : String(value)}
          onSave={(v) => onSave(v)}
          kind={field.field_type === "phone" ? "tel" : field.field_type}
          label={field.label}
        />
      );
    case "dropdown":
      return (
        <InlineEditSelect
          value={value == null ? "" : String(value)}
          options={[
            { value: "", label: "—" },
            ...field.options.map((o) => ({ value: o, label: o })),
          ]}
          onSave={(v) => onSave(v === "" ? null : v)}
          label={field.label}
        />
      );
    case "multiselect": {
      const current = Array.isArray(value) ? (value as string[]).join(", ") : "";
      return (
        <InlineEditField
          value={current}
          onSave={(v) => onSave(v == null ? [] : v.split(",").map((s) => s.trim()).filter(Boolean))}
          label={field.label}
          placeholder="comma-separated"
        />
      );
    }
    default:
      return (
        <InlineEditField
          value={value == null ? null : String(value)}
          onSave={(v) => onSave(v)}
          label={field.label}
        />
      );
  }
}

